import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, inr, buildWaLink } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { Button } from "@/components/ui/button";
import { Trophy, Medal, Copy, MessageCircle, RefreshCw, Send } from "lucide-react";
import { NudgeDialog } from "@/components/admin/NudgeDialog";

const PERIODS = [["today", "Today"], ["week", "This week"], ["month", "This month"], ["all", "All time"]];
const MEDAL = ["text-gold", "text-slate-400", "text-amber-700"];

export default function Leaderboard() {
  const { settings } = useSettings();
  const [period, setPeriod] = useState("month");
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const load = () => { api.get(`/admin/leaderboard?period=${period}`).then(({ data }) => setRows(data.rows)); api.get("/admin/daily-summary").then(({ data }) => setSummary(data)); };
  useEffect(load, [period]);
  const top = rows[0];
  const [nudge, setNudge] = useState(null);

  return (
    <div className="space-y-6" data-testid="leaderboard-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><h1 className="font-heading text-4xl font-semibold">Team Leaderboard</h1><p className="text-muted-foreground">Ranked by revenue, bookings and conversions.</p></div>
        <div className="inline-flex rounded-full border bg-card p-0.5" data-testid="leaderboard-period">
          {PERIODS.map(([k, l]) => <button key={k} type="button" onClick={() => setPeriod(k)} className={`px-3 py-1 rounded-full text-sm transition-colors ${period === k ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`} data-testid={`period-${k}`}>{l}</button>)}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          {top && top.revenue > 0 && (
            <div className="rounded-2xl bg-primary text-primary-foreground p-6 flex items-center gap-5" data-testid="leaderboard-top">
              <div className="h-16 w-16 rounded-2xl bg-gold text-black flex items-center justify-center"><Trophy className="h-8 w-8" /></div>
              <div><p className="text-xs uppercase tracking-widest opacity-80">Top performer</p><p className="font-heading text-3xl">{top.name} <span className="text-base opacity-80">· {top.employee_code}</span></p><p className="text-sm opacity-90">{inr(top.revenue)} revenue · {top.bookings} bookings · {top.conversion}% conversion</p></div>
            </div>
          )}
          <div className="rounded-xl border bg-card overflow-x-auto">
            <table className="w-full text-sm min-w-[760px]" data-testid="leaderboard-table">
              <thead className="bg-muted/50 text-left"><tr>{["#", "Employee", "Leads", "Converted", "Conv %", "Bookings", "Pax", "Revenue", "Collected", "Follow-ups due", ""].map((h) => <th key={h} className="p-3 whitespace-nowrap">{h}</th>)}</tr></thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.employee_id} className="border-t hover:bg-muted/30" data-testid="leaderboard-row">
                    <td className="p-3">{i < 3 ? <Medal className={`h-5 w-5 ${MEDAL[i]}`} /> : <span className="text-muted-foreground">{r.rank}</span>}</td>
                    <td className="p-3"><p className="font-medium">{r.name}</p><p className="text-xs text-muted-foreground">{r.employee_code}{r.status !== "Active" ? ` · ${r.status}` : ""}</p></td>
                    <td className="p-3">{r.leads}</td><td className="p-3">{r.converted}</td><td className="p-3">{r.conversion}%</td><td className="p-3">{r.bookings}</td><td className="p-3">{r.travellers}</td>
                    <td className="p-3 font-mono font-medium">{inr(r.revenue)}</td><td className="p-3 font-mono">{inr(r.collected)}</td>
                    <td className="p-3">{r.followups_due > 0 ? <span className="rounded-full bg-red-500/10 text-red-600 px-2 py-0.5 text-xs font-medium">{r.followups_due}</span> : <span className="text-muted-foreground">0</span>}</td>
                    <td className="p-3 text-right"><Button size="sm" variant="outline" className="h-7 rounded-full text-xs" onClick={() => setNudge(r)} data-testid="nudge-btn"><Send className="h-3.5 w-3.5 mr-1" /> Message</Button></td>
                  </tr>
                ))}
                {!rows.length && <tr><td colSpan={11} className="p-10 text-center text-muted-foreground">No employees yet. Add them under Employees.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        <div className="rounded-2xl border bg-card p-5 space-y-4 h-fit" data-testid="daily-summary">
          <div className="flex items-center justify-between"><h3 className="font-heading text-2xl">Daily Summary</h3><Button variant="ghost" size="icon" onClick={load}><RefreshCw className="h-4 w-4" /></Button></div>
          {summary && (
            <>
              <div className="grid grid-cols-2 gap-2 text-sm">
                {[["New leads", summary.new_leads], ["Follow-ups due", summary.followups_due], ["Confirmed today", summary.confirmed_today], ["Awaiting verification", summary.pending_verification]].map(([l, v]) => (
                  <div key={l} className="rounded-xl bg-muted p-3"><p className="text-xs text-muted-foreground">{l}</p><p className="font-heading text-2xl">{v}</p></div>
                ))}
              </div>
              <pre className="whitespace-pre-wrap text-xs bg-muted/60 rounded-xl p-3 font-sans leading-relaxed max-h-72 overflow-auto" data-testid="daily-summary-text">{summary.text}</pre>
              <div className="grid grid-cols-2 gap-2">
                <Button variant="outline" className="rounded-full" onClick={() => { navigator.clipboard.writeText(summary.text); toast.success("Summary copied"); }} data-testid="summary-copy"><Copy className="h-4 w-4 mr-1" /> Copy</Button>
                <a href={buildWaLink(settings.whatsapp_number, summary.text)} target="_blank" rel="noreferrer"><Button className="w-full rounded-full bg-[#25D366] hover:bg-[#1da851] text-white" data-testid="summary-whatsapp"><MessageCircle className="h-4 w-4 mr-1" /> WhatsApp</Button></a>
              </div>
            </>
          )}
        </div>
      </div>
      <NudgeDialog emp={nudge} onClose={() => setNudge(null)} />
    </div>
  );
}
